/**
 * Schedule Start Dialog (Redaktionsplan)
 *
 * Plan a campaign launch for a future date. Shows the account's free-from
 * forecast as slot advice ("voraussichtlich frei ab X" + one-click adopt)
 * and warns when the chosen date lands before the account frees up.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { useTranslation } from 'react-i18next';
import { getCampaignSchedule, scheduleCampaignStart, AccountTimeline } from '../../../API/campaign-schedule';

interface Props {
  open: boolean;
  campaignId: string;
  campaignName: string;
  linkedinAccountId: string;
  currentScheduledStartAt?: string | null;
  onClose(): void;
  onScheduled(scheduledStartAt: string): void;
}

/** Next workday (Mon-Fri) at 08:00 local time, as datetime-local value. */
function defaultStartValue(): string {
  const d = new Date();
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() === 0 || d.getDay() === 6);
  d.setHours(8, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ScheduleStartDialog: React.FC<Props> = ({
  open,
  campaignId,
  campaignName,
  linkedinAccountId,
  currentScheduledStartAt,
  onClose,
  onScheduled,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>(() =>
    currentScheduledStartAt ? toLocalInputValue(currentScheduledStartAt) : defaultStartValue()
  );
  const [accountTimeline, setAccountTimeline] = useState<AccountTimeline | null>(null);
  const [loadingSlot, setLoadingSlot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoadingSlot(true);
    getCampaignSchedule()
      .then((schedule) => {
        setAccountTimeline(schedule.timeline.find((a) => a.accountId === linkedinAccountId) || null);
      })
      .catch(() => setAccountTimeline(null))
      .finally(() => setLoadingSlot(false));
  }, [open, linkedinAccountId]);

  const freeFrom = accountTimeline?.freeFrom || null;
  const freeFromIsDate = !!freeFrom && freeFrom !== 'sofort';

  const chosenBeforeFree = useMemo(() => {
    if (!freeFromIsDate || !value) return false;
    return value.slice(0, 10) < (freeFrom as string);
  }, [freeFromIsDate, freeFrom, value]);

  const adoptSlot = () => {
    if (!freeFromIsDate) return;
    setValue(`${freeFrom}T08:00`);
  };

  const handleSubmit = async () => {
    if (!value || submitting) return;
    const startAt = new Date(value);
    if (Number.isNaN(startAt.getTime()) || startAt.getTime() <= Date.now()) {
      setError(t('scheduleDialogPastDate', 'Bitte einen Zeitpunkt in der Zukunft wählen.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await scheduleCampaignStart(campaignId, startAt.toISOString());
      onScheduled(result.scheduledStartAt);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || t('scheduleDialogFailed', 'Planung fehlgeschlagen — bitte erneut versuchen.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{t('scheduleDialogTitle', 'Kampagnenstart planen')}</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            {t('scheduleDialogHint', '"{{name}}" bleibt fertig vorbereitet liegen und startet automatisch zum gewählten Zeitpunkt.', {
              name: campaignName,
            })}
          </Typography>

          {loadingSlot ? (
            <Stack direction="row" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">
                {t('scheduleDialogLoadingSlot', 'Prüfe Account-Auslastung…')}
              </Typography>
            </Stack>
          ) : freeFrom ? (
            <Alert severity={freeFrom === 'sofort' ? 'success' : 'info'} icon={<EventIcon fontSize="small" />}>
              {freeFrom === 'sofort'
                ? t('scheduleDialogFreeNow', 'Der Account hat aktuell keine laufende Kampagne — jeder Zeitpunkt passt.')
                : t('scheduleDialogFreeFrom', 'Der Account ist voraussichtlich ab {{date}} frei.', {
                    date: new Date(freeFrom as string).toLocaleDateString('de-DE'),
                  })}
              {freeFromIsDate && (
                <Button size="small" onClick={adoptSlot} sx={{ ml: 1 }}>
                  {t('scheduleDialogAdoptSlot', 'Diesen Slot nehmen')}
                </Button>
              )}
            </Alert>
          ) : null}

          <TextField
            label={t('scheduleDialogDate', 'Startzeitpunkt')}
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          {chosenBeforeFree && (
            <Alert severity="warning">
              {t(
                'scheduleDialogConflict',
                'Der gewählte Start liegt vor dem voraussichtlichen Freiwerden des Accounts — die Kampagnen teilen sich dann die Tageskapazität (Gewichtung prüfen).'
              )}
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('cancel', 'Abbrechen')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!value || submitting}
          startIcon={submitting ? <CircularProgress size={14} /> : <EventIcon />}
        >
          {t('scheduleDialogSubmit', 'Start planen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleStartDialog;
